import { IconBatteryLow } from 'components/icons/IconBatteryLow'
import { renderToStaticMarkup } from 'react-dom/server'
import { svg64 } from 'svg64'
import { colors } from '../../../../settings/colors'
import type { TEquipmentType, TNodeDeviceType } from '../../store/serviceCenterStore'
import type { TNodeServiceIssueSummary, TNodeStatus, TNodeType } from '../../types'
import { DEVICE_TYPE_ICONS } from './deviceTypes'
import { EQUIPMENT_TYPE_ICONS, EQUIPMENT_TYPE_INNER_ICONS } from './equipmentTypes'
import { NODE_STATUS_COLORS } from './getActiveNodeStatus'
import { getNodeEquipmentType } from './getNodeEquipmentType'

// Local cache for node markers icons
const NODE_MARKERS_CACHE: { [key: string]: google.maps.Icon } = {}
const EQUIPMENT_ICON_WIDTH = 30
const EQUIPMENT_ICON_HEIGHT = 30
const DEVICE_BANNER_OFFSET = [3.5, 4.5]
const DEVICE_ICON_OFFSET = [EQUIPMENT_ICON_WIDTH - 4, 4]
const DEVICE_ICON_WIDTH = 14
const DEVICE_ICON_SPACING = 3
const STROKE_WIDTH = 2

type MarkerOptions = {
  selected?: boolean
  showBubble?: boolean
  deviceTypes?: TNodeDeviceType[]
  serviceIssues?: TNodeServiceIssueSummary[]
  targetInsect?: string | null
  isNodeIconDetailBadgeEnabled?: boolean
}

export const getNodeMapMarker = (
  nodeType: TNodeType,
  status: TNodeStatus,
  options: MarkerOptions = {},
): google.maps.Icon => {
  const {
    selected,
    showBubble,
    deviceTypes = [],
    serviceIssues,
    targetInsect,
    isNodeIconDetailBadgeEnabled,
  } = options

  const serviceIssueNodeIdentifier = serviceIssues?.[0]?.nodeIdentifier

  const cacheKey = `${nodeType}-${status}-${selected}-${showBubble}-${isNodeIconDetailBadgeEnabled}-${deviceTypes.join(
    '-',
  )}-${targetInsect}-${serviceIssueNodeIdentifier}`

  // Return cached icon if already exists
  if (NODE_MARKERS_CACHE[cacheKey]) return NODE_MARKERS_CACHE[cacheKey]

  // Get corresponding equipment type for node type
  const equipmentType = getNodeEquipmentType(nodeType)

  if (!equipmentType) throw new Error(`Unsupported node type: ${nodeType}!`)

  // Create SVG icon for equipment type (color shape + inner icon)
  const equipmentIcon = buildEquipmentIconSVG(equipmentType, status, serviceIssues)
  const strokeColor = selected ? 'yellow' : 'white'

  let bubble
  let bubbleOffset = [-4, 0]

  // Custom bubble offset for specific equipment types
  if (equipmentType === 'repeater') {
    bubbleOffset = [-8, 0]
  }

  // Create red bubble SVG element, if needed
  if (showBubble) {
    bubble = `<g transform="translate(${bubbleOffset[0]} ${bubbleOffset[1]})" stroke-width="2">
      <circle cx="26" cy="6" r="5" fill="red" />
    </g>`
  }

  let deviceBanner
  let trapBanner

  const showDeviceIcons = !!deviceTypes?.length && selected

  // Build device banner SVG string if node has devices
  if (deviceTypes?.length && isNodeIconDetailBadgeEnabled) {
    deviceBanner = buildDeviceBannerSVG(equipmentType, deviceTypes, showDeviceIcons, strokeColor)
  }

  if (nodeType.includes('trp') && targetInsect && isNodeIconDetailBadgeEnabled) {
    trapBanner = buildTrapBannerSVG(targetInsect)
  }

  let width = EQUIPMENT_ICON_WIDTH
  let height = EQUIPMENT_ICON_HEIGHT

  if (deviceTypes?.length) {
    // If node has devices, increase marker width to fit the banner
    if (showDeviceIcons) {
      width += deviceTypes.length * (DEVICE_ICON_WIDTH + DEVICE_ICON_SPACING) + 10
    } else {
      width += 20
    }
  }

  if (trapBanner && targetInsect) {
    width += 15 + targetInsect.length * 6
  }

  const viewBox = `-2 -2 ${width} ${height}`

  const svgString = `
    <svg width="${width}" height="${height}" viewBox="${viewBox}" xmlns="http://www.w3.org/2000/svg">
      ${deviceBanner}
      ${trapBanner}
    
      <g stroke="${strokeColor}">
        ${equipmentIcon}
        
        ${bubble}
      </g>
      
      </svg>`

  // Transform SVG to base64 url
  const base64Svg = svg64(svgString)
  const anchor = new google.maps.Point(EQUIPMENT_ICON_WIDTH / 2, EQUIPMENT_ICON_HEIGHT / 2)

  const image: google.maps.Icon = {
    url: base64Svg,
    size: new google.maps.Size(width, height),
    anchor,
  }

  // Cache marker icon
  NODE_MARKERS_CACHE[cacheKey] = image

  return image
}

const buildEquipmentIconSVG = (
  equipmentType: TEquipmentType,
  status: TNodeStatus,
  serviceIssues?: TNodeServiceIssueSummary[],
) => {
  // Extract SVG content from the icon component
  const EquipmentOuterIcon = EQUIPMENT_TYPE_ICONS[equipmentType]
  const outerSvgElement = EquipmentOuterIcon().props.children
  const outerViewBoxString = EquipmentOuterIcon().props.viewBox
  const outerIconWidth = Number(outerViewBoxString.split(' ')[2])
  const outerIconHeight = Number(outerViewBoxString.split(' ')[3])
  const outerSvgContent = renderToStaticMarkup(outerSvgElement)
  const fillColor = NODE_STATUS_COLORS[status]

  let outerIconOffset = [0, 0]

  const outerIconSize = EQUIPMENT_ICON_WIDTH - STROKE_WIDTH * 2

  // Scale outer icon to fit in the marker
  let outerIconScale = outerIconSize / Math.max(outerIconWidth, outerIconHeight)

  // Custom outer icon size for specific equipment types
  if (equipmentType === 'gateway') {
    outerIconScale = outerIconScale * 0.85

    outerIconOffset = [0, 4]
  }

  // Create inner icon SVG string
  const innerSvgContent = buildInnerIconSVG(equipmentType, serviceIssues)

  return `
    <g color="${fillColor}" stroke-width="${STROKE_WIDTH}" transform="translate(${outerIconOffset[0]} ${outerIconOffset[1]}) scale(${outerIconScale} ${outerIconScale})">
      ${outerSvgContent}
      ${innerSvgContent}
    </g>`
}

const buildInnerIconSVG = (equipmentType: TEquipmentType, serviceIssues?: TNodeServiceIssueSummary[]) => {
  // Extract SVG content from the icon component
  let EquipmentInnerIcon = EQUIPMENT_TYPE_INNER_ICONS[equipmentType]
  let innerIconScale = 0.8
  let lowBattery = false

  // Custom icon scale for specific equipment types
  if (equipmentType === 'trap') innerIconScale = 0.5

  if (serviceIssues && serviceIssues.length) {
    const isBatteryLow = serviceIssues.some((issue) =>
      issue.nodeServiceIssueTypeTitle.includes('Low Battery'),
    )

    if (isBatteryLow) {
      EquipmentInnerIcon = IconBatteryLow

      innerIconScale = 1.5

      lowBattery = true
    }
  }

  if (!EquipmentInnerIcon) return ''

  const innerSvgElement = EquipmentInnerIcon().props.children
  const innerViewBoxString = EquipmentInnerIcon().props.viewBox
  const innerIconWidth = Number(innerViewBoxString.split(' ')[2]) * innerIconScale
  const innerIconHeight = Number(innerViewBoxString.split(' ')[3]) * innerIconScale

  let translateX = EQUIPMENT_ICON_WIDTH / 2 - innerIconWidth / 2
  let translateY = EQUIPMENT_ICON_HEIGHT / 2 - innerIconHeight / 2

  // Custom icon offset for specific equipment types
  if (lowBattery) {
    switch (equipmentType) {
      case 'gateway':
        translateX += -4.5

        translateY += 2

        break

      case 'trap':
        translateX += 1

        translateY += 9

        break

      case 'repeater':
        translateY += 11

        translateX += 1

        innerIconScale = 1.2

        break

      case 'dispenser':
        translateY += 4

        translateX -= 2

        break

      case 'station':
        translateX += -1

        translateY += 4.5

        break

      default:
        break
    }
  } else if (equipmentType === 'station') {
    translateY += 2

    translateX += 4
  } else if (equipmentType === 'trap') {
    translateX += 6

    translateY += 6
  }

  return `
    <g
      color="${colors.midnight}"
      stroke="transparent"
      transform="translate(${translateX} ${translateY}) scale(${innerIconScale} ${innerIconScale})"
    >
      ${renderToStaticMarkup(innerSvgElement)}
    </g>`
}

const buildDeviceBannerSVG = (
  equipmentType: TEquipmentType,
  nodeDeviceTypes: TNodeDeviceType[],
  showIcons?: boolean,
  strokeColor = 'white',
) => {
  if (!nodeDeviceTypes?.length) return ''

  const bannerHeight = 16
  const bannerBackgroundColor = colors.midnight

  let translateX = DEVICE_BANNER_OFFSET[0]
  let translateY = DEVICE_BANNER_OFFSET[1]

  // Custom banner offset for specific equipment types
  if (equipmentType === 'repeater') {
    translateX -= 4

    translateY += 2
  }

  if (!showIcons) {
    const bannerWidth = EQUIPMENT_ICON_WIDTH + 1

    // Show devices count in the banner
    return `
      <g transform="translate(${translateX} ${translateY})">
        <rect
          x="10" y="0"
          width="${bannerWidth}"
          height="${bannerHeight}"
          rx="${bannerHeight / 2}"
          fill="${bannerBackgroundColor}"
          stroke="${strokeColor}"
          stroke-width="${STROKE_WIDTH}"
         />
        <text
          x="${DEVICE_ICON_OFFSET[0]}" y="13" 
          class="small" 
          fill="white" 
          font-family="Helvetica" 
          font-weight="400" 
          font-size="14"
        >
          ${nodeDeviceTypes.length}
        </text>
      </g>`
  }

  // Retrieve device icons for each device type
  const deviceIcons = nodeDeviceTypes.map((deviceType) => DEVICE_TYPE_ICONS[deviceType])

  // Transform each icon to SVG string and group them together
  const deviceIconsSvg = deviceIcons
    .filter((deviceIcon) => !!deviceIcon)
    .map(transformIconToSVG)
    .join('')

  const bannerWidth = 22 + nodeDeviceTypes.length * (DEVICE_ICON_WIDTH + DEVICE_ICON_SPACING)

  return `
    <g transform="translate(${translateX} ${translateY})">
      <rect
        x="10" y="2"
        width="${bannerWidth}"
        height="${bannerHeight}"
        rx="${bannerHeight / 2}"
        fill="${bannerBackgroundColor}" 
        stroke="${strokeColor}"
        stroke-width="${STROKE_WIDTH}"
      />
      
      ${deviceIconsSvg}
    </g>`
}

const buildTrapBannerSVG = (targetInsect: string) => {
  const bannerHeight = 16
  const bannerBackgroundColor = colors.midnight

  let translateX = -2

  const translateY = 5.5

  let bannerWidth = 30 + targetInsect.length * 6

  // Show devices count in the banner
  return `
      <g transform="translate(${translateX} ${translateY})">
        <rect
          x="10" y="0"
          width="${bannerWidth}"
          height="${bannerHeight}"
          rx="${bannerHeight / 2}"
          fill="${bannerBackgroundColor}"
          stroke="white"
          stroke-width="${STROKE_WIDTH}"
         />
        <text
          x="${28}" y="11" 
          class="small" 
          fill="white" 
          font-family="Helvetica" 
          font-weight="400" 
          font-size="10.5"
        >
          ${targetInsect}
        </text>
      </g>`
}

const transformIconToSVG = (Icon: () => React.JSX.Element, index: number): string => {
  const iconElement = Icon().props.children
  const viewBoxString = Icon().props.viewBox
  const iconWidth = Number(viewBoxString.split(' ')[2])
  const iconHeight = Number(viewBoxString.split(' ')[3])
  // Scale each icon to fit in the banner
  const scale = DEVICE_ICON_WIDTH / Math.max(iconWidth, iconHeight)
  // Offset each icon so they line up horizontally one after the other
  const translateX = DEVICE_ICON_OFFSET[0] + index * (DEVICE_ICON_WIDTH + DEVICE_ICON_SPACING)

  let translateY = DEVICE_ICON_OFFSET[1]

  // Custom icon offset for specific devices
  if (Icon.name === 'IconWindMachine') translateY -= 2
  else if (Icon.name === 'IconLWS') translateY -= 1
  else if (Icon.name === 'IconSloshingWaterInWaterDrop') translateY -= 1
  else if (Icon.name === 'IconDendrometer') translateY += 2

  return `
    <g color="white" transform="translate(${translateX} ${translateY}) scale(${scale} ${scale})">
      ${renderToStaticMarkup(iconElement)}
    </g>`
}
